import { Collapse } from '@mui/material'
import cn from 'classnames'
import React, {
  KeyboardEvent,
  ReactElement,
  ReactNode,
  useId,
  useState,
} from 'react'
import { useTranslate } from 'src/localization/Trns'

import style from './SettingsDisclosure.style'

type SettingsDisclosureProps = {
  /** Primary heading of the row (already translated). */
  title: string
  /** Secondary line beneath the title — shown open or collapsed. */
  summary?: ReactNode
  /** Collapsed affordance text left of the chevron (e.g. "Edit", "View", "Manage"). Swaps to "Hide" when open. */
  actionLabel: string
  defaultOpen?: boolean
  children: ReactNode
  className?: string
}

function ChevronDownIcon(): ReactElement {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M4 6l4 4 4-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * Box-free expand/collapse row used by the redesigned Account Settings tiles
 * (EX-40354). Collocated title + summary on the left; a text affordance
 * (`actionLabel` → "Hide" when open) plus a secondary-grey chevron on the
 * right, over a hairline divider. Mirrors the design-render `Disclosure`.
 */
export default function SettingsDisclosure(
  props: SettingsDisclosureProps,
): ReactElement {
  const {
    title,
    summary,
    actionLabel,
    defaultOpen = false,
    children,
    className = '',
  } = props
  const translate = useTranslate()
  const contentId = useId()
  const [open, setOpen] = useState(defaultOpen)

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      event.preventDefault()
      setOpen(true)
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      event.preventDefault()
      setOpen(false)
    }
  }

  return (
    <div className={cn('SettingsDisclosure', className)} css={style}>
      <button
        type="button"
        className="SettingsDisclosure-header"
        aria-expanded={open}
        // The content is unmounted while collapsed (Collapse unmountOnExit), so
        // only advertise the controls relationship when the target exists.
        aria-controls={open ? contentId : undefined}
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={handleKeyDown}
      >
        <span className="SettingsDisclosure-titleCol">
          <span className="SettingsDisclosure-title">{title}</span>
          {summary && (
            <span className="SettingsDisclosure-summary">{summary}</span>
          )}
        </span>
        <span className="SettingsDisclosure-action">
          <span className="SettingsDisclosure-actionLabel">
            {open ? translate('Hide') : actionLabel}
          </span>
          <span
            aria-hidden="true"
            className={cn('SettingsDisclosure-chevron', { open })}
          >
            <ChevronDownIcon />
          </span>
        </span>
      </button>
      <Collapse in={open} unmountOnExit>
        <div id={contentId} className="SettingsDisclosure-content">
          {children}
        </div>
      </Collapse>
    </div>
  )
}
